import { Empty, Spin } from 'antd'
import { CategoryScale, Chart, registerables } from 'chart.js'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { useApi } from '../../api/useApi'
import { useLatestRunMonth } from '../../api/latestRunApi'
import { setUtförareCases } from '../../api/utförareApi'
import ViewHeader from '../../components/viewHeader/ViewHeader'
import FilterToolbar from '../../components/filterToolbar/FilterToolbar'
import shortenText from '../arendeFordelning/components/tableUtils'
import { grafTooltip, perBrukare } from './grafTooltip'
import { getDeviation, getFlagLevel, getUtforareStartDate, getUtforareEndDate, setUtforareFilter } from '../../store/utforareFilterSlice'
import { IBrukareFlag, IMedarbetareFlag, IUtförare, getUtförare, setUtförare } from '../../store/utförareSlice'
Chart.register(...registerables)
Chart.register(CategoryScale)

const ABSOLUTE_MIN_CHART_HEIGHT = 300
const BOTTOM_VIEWPORT_MARGIN = 24
const PX_PER_BAR = 28
const LABEL_MAX_LENGTH = 18

export interface IDataSet {
  labels: string
  data: number[]
  borderColor: string
  backgroundColor: string
}

export interface IChart {
  labels: string[]
  datasets: IDataSet[]
}

interface IMenu {
  value: string | number
  label: string | number
}

function createGraphY(data: any) {
  const labels: string[] = []
  data.forEach((element: any) => {
    labels.push(element.namn)
  })
  return labels
}

function uniqueLevelItems(data: any) {
  const allDeviations: number[] = data.map((utförare: IUtförare) => {
    return [
      ...(utförare.brukare ? utförare.brukare.map((brukare: IBrukareFlag) => brukare.nivå) : []),
      ...(utförare.medarbetare ? utförare.medarbetare.map((medarbetare: IMedarbetareFlag) => medarbetare.nivå) : []),
    ]
  })

  const unique = Array.from(new Set(allDeviations.flat()))
  return unique.sort((n1, n2) => n1 - n2)
}

function uniqueDeviation(data: any) {
  const allDeviations: string[] = data.map((utförare: IUtförare) => {
    return [
      ...(utförare.brukare ? utförare.brukare.map((brukare: IBrukareFlag) => brukare.flaggnamn) : []),
      ...(utförare.medarbetare ? utförare.medarbetare.map((medarbetare: IMedarbetareFlag) => medarbetare.flaggnamn) : []),
    ]
  })

  const res = Array.from(new Set(allDeviations.flat()))
  return res
}

function generateGraphData(labels: string[], data: number[], checked: boolean) {
  const graph: IChart = {
    labels: labels,
    datasets: [
      {
        labels: checked ? 'Summa av risknivåer' : 'Antal flaggor',
        data: data,
        borderColor: '#1E73A8FF',
        backgroundColor: '#1E73A833',
      },
    ],
  }

  return graph
}

function generateMenu(deviations: number[]) {
  const menuItems: IMenu[] = []
  deviations.forEach((numb) => {
    menuItems.push({ value: Number(numb), label: Number(numb) })
  })
  return menuItems
}

function generateDeviationMenu(deviations: string[]) {
  const menuItems: IMenu[] = []
  deviations.forEach((numb) => {
    menuItems.push({ value: String(numb), label: String(numb) })
  })
  return menuItems
}

function getGraphTitle(graphflagga: number, checked: boolean) {
  if (graphflagga === 1) return checked ? 'Antal flaggor' : 'Antal flaggor (exkl. risknivå)'
  if (graphflagga === 2) return checked ? 'Antal flaggor per brukare' : 'Antal flaggor per brukare (exkl. risknivå)'
  return ''
}

async function getTableData(startDate: string, endDate: string) {
  const data = await setUtförareCases(useApi(), startDate, endDate)
  return data
}

function Utforare() {
  const [ammountData, setAmmountData] = useState<IChart>({
    labels: [],
    datasets: [
      {
        labels: 'Antal flaggor',
        data: [],
        borderColor: '#1E73A8FF',
        backgroundColor: '#1E73A833',
      },
    ],
  })
  const [brukareData, setBrukareData] = useState<IChart>({
    labels: [],
    datasets: [
      {
        labels: 'Antal flaggor per brukare',
        data: [],
        borderColor: '#1E73A8FF',
        backgroundColor: '#1E73A833',
      },
    ],
  })
  // Vyn visar alltid det riskvägda värdet (flaggornas risknivå tas alltid med).
  const checked = true
  const [deviation, setDeviation] = useState<string[]>(useSelector(getDeviation))
  const [flagLevel, setFlagLevel] = useState<number[]>(useSelector(getFlagLevel))
  // #47: datepicker-default = senaste körningens månad (ej nuvarande), men användarens egna val
  // bibehålls i utforareFilterSlice (överlever navigering). null = inget val ännu → senaste körningen.
  const { ready: lrReady, from: lrFrom, to: lrTo } = useLatestRunMonth()
  const sliceStart = useSelector(getUtforareStartDate)
  const sliceEnd = useSelector(getUtforareEndDate)
  const startDate = sliceStart ?? lrFrom
  const endDate = sliceEnd ?? lrTo
  const [fetchingData, setFetchingData] = useState(false)
  const [sortBy, setSortBy] = useState<'alpha' | 'value'>('alpha')
  // const [data, setData] = useState<IUtförare[]>()
  const dispatch = useDispatch()
  const navigate = useHistory()

  const data = useSelector(getUtförare)
  // Per-utförare-värde = riskvägt antal flaggor per brukare (samma logik som vänstergrafen),
  // med nuvarande avvikelse/risknivå-filter. Används för värde-sortering (störst överst).
  const brukarePerValue = (element: any) => {
    let temp = 0
    const acc = (arr: any[]) =>
      (arr || []).forEach((x: any) => {
        const dOk = deviation.length === 0 || deviation.includes(x.flaggnamn)
        const lOk = flagLevel.length === 0 || flagLevel.includes(Number(x.nivå))
        if (dOk && lOk) temp += x.nivå
      })
    acc(element.brukare)
    acc(element.medarbetare)
    return element.brukare_count ? temp / element.brukare_count : 0
  }
  const sortedData = useMemo(() => {
    if (!data) return data
    const arr = [...data]
    return sortBy === 'value'
      ? arr.sort((a: IUtförare, b: IUtförare) => brukarePerValue(b) - brukarePerValue(a))
      : arr.sort((a: IUtförare, b: IUtförare) => a.namn.localeCompare(b.namn, 'sv'))
  }, [data, sortBy, deviation, flagLevel])
  const chartWrapperRef = useRef<HTMLDivElement>(null)
  const senasteFråga = useRef(0)
  const [viewportMinHeight, setViewportMinHeight] = useState(ABSOLUTE_MIN_CHART_HEIGHT)

  useLayoutEffect(() => {
    const compute = () => {
      if (!chartWrapperRef.current) return
      const top = chartWrapperRef.current.getBoundingClientRect().top
      const available = window.innerHeight - top - BOTTOM_VIEWPORT_MARGIN
      setViewportMinHeight(Math.max(ABSOLUTE_MIN_CHART_HEIGHT, available))
    }
    compute()
    const observer = new ResizeObserver(compute)
    observer.observe(document.documentElement)
    return () => observer.disconnect()
  }, [])

  // Mät höjden igen när laddningen är klar (charts monteras/avmonteras).
  // useEffect + requestAnimationFrame säkerställer att webbläsaren lagt ut layouten innan mätning.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      if (!chartWrapperRef.current) return
      const top = chartWrapperRef.current.getBoundingClientRect().top
      const available = window.innerHeight - top - BOTTOM_VIEWPORT_MARGIN
      setViewportMinHeight(Math.max(ABSOLUTE_MIN_CHART_HEIGHT, available))
    })
    return () => cancelAnimationFrame(raf)
  }, [fetchingData])

  const chartHeight = Math.max(viewportMinHeight, (sortedData?.length ?? 0) * PX_PER_BAR)

  function handleDatesChange(range: any) {
    const start = range[0].startOf('month').format().split('T')[0]
    const end = range[1].endOf('month').format().split('T')[0]
    dispatch(setUtforareFilter({ deviation: deviation, flagLevel: flagLevel, startDate: start, endDate: end }))
  }

  function handleOpenChange() {
    return undefined
  }

  const handleLevelChange = (value: any) => {
    setFlagLevel(value.map(Number))
    dispatch(setUtforareFilter({ deviation: deviation, flagLevel: value.map(Number), startDate: startDate, endDate: endDate }))
  }

  const handleDeviationChange = (value: any) => {
    setDeviation(value)
    dispatch(setUtforareFilter({ deviation: value, flagLevel: flagLevel, startDate: startDate, endDate: endDate }))
  }

  const resetAllFilters = () => {
    setDeviation([])
    setFlagLevel([])
    dispatch(setUtforareFilter({ deviation: [], flagLevel: [] }))
  }

  function generateOptions(graphflagga: number, checked: boolean) {
    return {
      indexAxis: 'y' as const,
      maintainAspectRatio: false,
      elements: {
        bar: {
          borderWidth: 1.0,
        },
      },
      scales: {
        // Siffrorna i samma format som utförarnamnen, inte Chart.js standard (12 px, grått).
        x: {
          ticks: {
            color: 'rgba(0, 0, 0, 0.85)',
            font: {
              family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
              size: 14,
              weight: 'normal' as const,
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: { display: false },
          ticks: {
            color: 'rgba(0, 0, 0, 0.85)',
            font: {
              family: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
              size: 14,
              weight: 'normal' as const,
            },
            callback: function (value: any) {
              const label = (this as any).getLabelForValue(value)
              return shortenText(label, LABEL_MAX_LENGTH)
            },
          },
        },
      },
      onClick: function (e: any, activeEls: any) {
        if (activeEls.length > 0) {
          let dataIndex = activeEls[0].index
          let label = e.chart.data.labels[dataIndex]
          navigate.push(`/app/utforare/utforare=${label}&from=${startDate}&to=${endDate}&flaglevel=${flagLevel.length > 0 ? flagLevel : null}&deviation=${deviation.length > 0 ? deviation : null}`)
        }
      },
      onHover: function (e: any, activeEls: any) {
        const target = e?.native?.target
        if (target) target.style.cursor = activeEls.length > 0 ? 'pointer' : 'default'
      },
      plugins: {
        legend: {
          display: false,
          onClick: () => null,
          labels: {
            color: 'rgb(255, 99, 132)',
          },
        },
        title: {
          display: false,
        },
        tooltip: {
          // Ingen färgruta: graferna har ingen förklaring som rutan kan peka på.
          ...grafTooltip(false),
          callbacks: {
            // Summan av risknivåer är ett heltal, flaggor per brukare har tre decimaler.
            label: (item: any) => (graphflagga === 2 ? perBrukare(item.parsed.x) : Number(item.parsed.x).toLocaleString('sv-SE')),
          },
        },
      },
    }
  }

  useEffect(() => {
    if (data) {
      const labels = createGraphY(sortedData)
      const xAxis1 = createGraphAmmountX(sortedData)
      const xAxis2 = createGraphAmmountBrukareX(sortedData)

      setAmmountData(generateGraphData(labels, xAxis1, checked))
      setBrukareData(generateGraphData(labels, xAxis2, checked))
    }
  }, [data])

  function createGraphAmmountX(data: any) {
    const graphData: number[] = []
    if (deviation.length > 0 && flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(innerElement.nivå)) {
              temp += 1
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(Number(innerElement.nivå))) {
              temp += 1
            }
          })
        }
        graphData.push(temp)
      })
    } else if (deviation.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += 1
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += 1
            }
          })
        }
        graphData.push(temp)
      })
    } else if (flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare.length > 0 && element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += 1
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += 1
            }
          })
        }
        graphData.push(temp)
      })
    } else {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            temp += 1
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            temp += 1
          })
        }
        graphData.push(temp)
      })
    }
    return graphData
  }

  function createGraphAmmountBrukareX(data: any) {
    const graphData: number[] = []
    if (deviation.length > 0 && flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(innerElement.nivå)) {
              temp += 1
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(innerElement.nivå)) {
              temp += 1
            }
          })
        }
        graphData.push(temp / element.brukare_count)
      })
    } else if (deviation.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += 1
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += 1
            }
          })
        }
        graphData.push(temp / element.brukare_count)
      })
    } else if (flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += 1
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += 1
            }
          })
        }
        graphData.push(temp / element.brukare_count)
      })
    } else {
      data.forEach((element: any) => {
        var temp = 0
        if (element.brukare) {
          temp += element.brukare.length
          //graphData.push(element.brukare.length / element.brukare_count)
        }
        if (element.medarbetare) {
          temp += element.medarbetare.length
        }
        graphData.push(temp / element.brukare_count)
      })
    }
    return graphData
  }

  function createGraphLevelX(data: any) {
    const graphData: number[] = []
    if (deviation.length > 0 && flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        graphData.push(temp)
      })
    } else if (deviation.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += innerElement.nivå
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += innerElement.nivå
            }
          })
        }
        graphData.push(temp)
      })
    } else if (flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        graphData.push(temp)
      })
    } else {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            temp += innerElement.nivå
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            temp += innerElement.nivå
          })
        }
        graphData.push(temp)
      })
    }
    return graphData
  }

  function createGraphLevelBrukareX(data: any) {
    const graphData: number[] = []
    if (deviation.length > 0 && flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn) && flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        graphData.push(temp / element.brukare_count)
      })
    } else if (deviation.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += innerElement.nivå
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (deviation.includes(innerElement.flaggnamn)) {
              temp += innerElement.nivå
            }
          })
        }
        graphData.push(temp / element.brukare_count)
      })
    } else if (flagLevel.length > 0) {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            if (flagLevel.includes(innerElement.nivå)) {
              temp += innerElement.nivå
            }
          })
        }
        graphData.push(temp / element.brukare_count)
      })
    } else {
      data.forEach((element: any) => {
        let temp = 0
        if (element.brukare) {
          element.brukare.forEach((innerElement: any) => {
            temp += innerElement.nivå
          })
        }
        if (element.medarbetare) {
          element.medarbetare.forEach((innerElement: any) => {
            temp += innerElement.nivå
          })
        }
        graphData.push(temp / element.brukare_count)
      })
    }
    return graphData
  }

  useEffect(() => {
    if (!data) return

    if (checked) {
      const labels = createGraphY(sortedData)
      const xAxis1 = createGraphLevelX(sortedData)
      const xAxis2 = createGraphLevelBrukareX(sortedData)
      setAmmountData(generateGraphData(labels, xAxis1, checked))
      setBrukareData(generateGraphData(labels, xAxis2, checked))
    } else {
      const labels = createGraphY(sortedData)
      const xAxis1 = createGraphAmmountX(sortedData)
      const xAxis2 = createGraphAmmountBrukareX(sortedData)
      setAmmountData(generateGraphData(labels, xAxis1, checked))
      setBrukareData(generateGraphData(labels, xAxis2, checked))
    }
  }, [checked, deviation, flagLevel, data, sortBy])

  // Hämta data först när latest-run är klart → aldrig en hämtning med fallback-månaden (#47).
  // Bara svaret på den senaste frågan används (#101): byts perioden medan en hämtning pågår kan
  // svaren komma i omvänd ordning, och annars visades det som kom sist.
  useEffect(() => {
    if (!lrReady) return
    const nr = ++senasteFråga.current
    const fetchData = async () => {
      setFetchingData(true)
      const data = await getTableData(startDate, endDate)
      if (nr !== senasteFråga.current) return

      setFetchingData(false)
      dispatch(setUtförare(data))
    }
    fetchData()
  }, [lrReady, startDate, endDate])

  if (!lrReady) {
    return (
      <>
        <ViewHeader heading="Utförare" description="Denna vy sammanställer flaggor per utförare för att jämföra dem mot varandra." />
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
          <Spin />
        </div>
      </>
    )
  }

  return (
    <>
      <ViewHeader heading="Utförare" description="Denna vy sammanställer flaggor per utförare för att jämföra dem mot varandra." />
      <FilterToolbar
        startDate={startDate}
        endDate={endDate}
        onDatesChange={handleDatesChange}
        onOpenChange={handleOpenChange}
        filterSelects={
          data
            ? [
                {
                  key: 'avvikelse',
                  label: 'Välj flagga',
                  options: generateDeviationMenu(uniqueDeviation(data)),
                  value: deviation,
                  onChange: handleDeviationChange,
                },
                {
                  key: 'risknivå',
                  label: 'Välj risknivå',
                  options: generateMenu(uniqueLevelItems(data)),
                  value: flagLevel,
                  onChange: handleLevelChange,
                },
              ]
            : []
        }
        onResetFilters={resetAllFilters}
        sortOptions={[
          { value: 'alpha', label: 'Alfabetisk' },
          { value: 'value', label: 'Antal flaggor per brukare' },
        ]}
        sortValue={sortBy}
        onSortChange={(v) => setSortBy(v as 'alpha' | 'value')}
      />

      {fetchingData || (data && data.length > 0) ? (
        <div className="flex gap-6" style={{ marginTop: 30 }}>
          <div className="flex flex-1 min-w-0 flex-col">
            <h1 className="text-center" style={{ fontSize: 20, marginBottom: 6 }}>{getGraphTitle(2, checked)}</h1>
            <div ref={chartWrapperRef} style={{ height: chartHeight, position: 'relative' }}>
              {fetchingData ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', height: '100%', paddingTop: 80 }}>
                  <Spin />
                </div>
              ) : (
                <Bar options={generateOptions(2, checked)} data={brukareData} />
              )}
            </div>
          </div>
          <div className="flex flex-1 min-w-0 flex-col">
            <h1 className="text-center" style={{ fontSize: 20, marginBottom: 6 }}>{getGraphTitle(1, checked)}</h1>
            <div style={{ height: chartHeight, position: 'relative' }}>
              {fetchingData ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', height: '100%', paddingTop: 80 }}>
                  <Spin />
                </div>
              ) : (
                <Bar options={generateOptions(1, checked)} data={ammountData} />
              )}
            </div>
          </div>
        </div>
      ) : data ? (
        <div style={{ marginTop: 40, display: 'flex', justifyContent: 'center' }}>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      ) : null}
    </>
  )
}

export default Utforare
